// src/services/assistant/TokenBatcher.ts
/**
 * Token Batcher - Batches rapid token updates to reduce re-renders
 * 
 * During streaming, tokens arrive at ~100+/second. Without batching,
 * each token triggers a Zustand update and React re-render.
 * 
 * This batcher collects tokens and flushes them at ~60fps (16ms intervals),
 * reducing re-renders from hundreds to ~60 per second.
 */

import type { AgentId } from "@/types/assistant";
import { useAssistantStore } from "@/stores/assistantStore";

interface TokenUpdate {
  delta: string;
  agentId: AgentId;
  channel: "draft" | "final";
  streamId: string;
}

interface BatchKey {
  conversationId: string;
  agentId: AgentId;
  channel: "draft" | "final";
  streamId: string;
}

class TokenBatcher {
  private buffer: Map<string, TokenUpdate> = new Map();
  private timer: ReturnType<typeof requestAnimationFrame> | null = null;
  private isFlushScheduled = false;

  /**
   * Create a unique key for batching tokens
   */
  private makeKey(key: BatchKey): string {
    return `${key.conversationId}:${key.agentId}:${key.channel}:${key.streamId}`;
  }

  /**
   * Parse a batch key back to its components
   */
  private parseKey(key: string): BatchKey {
    const [conversationId, agentId, channel, streamId] = key.split(":");
    return {
      conversationId: conversationId!,
      agentId: agentId as AgentId,
      channel: channel as "draft" | "final",
      streamId: streamId!,
    };
  }

  /**
   * Add a token to the batch
   */
  add(
    conversationId: string,
    agentId: AgentId,
    delta: string,
    channel: "draft" | "final",
    streamId: string
  ): void {
    const key = this.makeKey({ conversationId, agentId, channel, streamId });
    const existing = this.buffer.get(key);

    if (existing) {
      // Append to existing buffer
      existing.delta += delta;
    } else {
      // Create new buffer entry
      this.buffer.set(key, { delta, agentId, channel, streamId });
    }

    // Schedule flush if not already scheduled
    if (!this.isFlushScheduled) {
      this.scheduleFlush();
    }
  }

  /**
   * Schedule a flush on the next animation frame (~16ms)
   */
  private scheduleFlush(): void {
    this.isFlushScheduled = true;
    this.timer = requestAnimationFrame(() => {
      this.flush();
      this.isFlushScheduled = false;
    });
  }

  /**
   * Flush all buffered tokens to the store
   */
  flush(): void {
    if (this.buffer.size === 0) return;

    const store = useAssistantStore.getState();

    // Process all buffered tokens
    this.buffer.forEach((update, key) => {
      const { conversationId } = this.parseKey(key);
      store.appendToken(
        conversationId,
        update.delta,
        update.agentId,
        update.channel,
        update.streamId
      );
    });

    // Clear the buffer
    this.buffer.clear();
  }

  /**
   * Force immediate flush (useful when stream ends)
   */
  flushImmediate(): void {
    if (this.timer) {
      cancelAnimationFrame(this.timer);
      this.timer = null;
    }
    this.isFlushScheduled = false;
    this.flush();
  }

  /**
   * Clear all pending tokens for a conversation (useful on abort)
   */
  clearConversation(conversationId: string): void {
    const keysToDelete: string[] = [];
    
    this.buffer.forEach((_, key) => {
      if (key.startsWith(`${conversationId}:`)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.buffer.delete(key));
  }

  /**
   * Check if there are pending tokens
   */
  hasPending(): boolean {
    return this.buffer.size > 0;
  }

  /**
   * Get count of pending updates (for debugging)
   */
  getPendingCount(): number {
    return this.buffer.size;
  }
}

// Singleton instance
export const tokenBatcher = new TokenBatcher();

export default tokenBatcher;

